import { useState } from "react";
import { UserState } from "../FormData/CrmFormData";
import { Button, Form, Col, Label } from "reactstrap";
import TextField from "../../../common/TextField";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import { RootState } from "../../../../ReduxToolkit/Store";
import { useSelector } from "react-redux";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { manageUserService } from "../../../../Services/CRMAdminServices/CRMStaffService";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import MobileField from "../../../common/MobileField";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { H3 } from "../../../../AbstractElements";
import { Eye, EyeOff } from "react-feather";

const AddStaff = () => {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState<any>(UserState);
    const [formErrors, setFormErrors] = useState<any>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useSafeNavigate();
    const { roles } = useSelector((state: RootState) => state.roles);

    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    // Transform roles data into dropdown format
    const roleOptions = roles.map((role: any) => ({
        value: role.roleID.toString(),
        label: role.roleName,
    }));

    const {
        mutate: manageUser,
        isPending: isManageUserPending,
    } = useMutationRequestQuery("manageUser", manageUserService, {
        onSuccess: () => {
            toast.success("Staff added successfully.");
            navigate('/CRMStaff');
            queryClient.invalidateQueries({ queryKey: ["getStaffs"] });
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.error || "Failed to add staff.";
            toast.error(errorMessage);
            
            // Handle API validation errors
            if (error?.response?.data?.errors) {
                const apiErrors = error.response.data.errors;
                setFormErrors(apiErrors);
            }
        },
    });

    const validateForm = () => {
        let errors: any = {};

        // Role validation
        if (!formData.roleID) {
            errors.roleID = "Role is required!";
        }

        // Name validations
        if (!formData.firstName?.trim()) {
            errors.firstName = "First Name is required!";
        } else if (formData.firstName.length < 2) {
            errors.firstName = "First Name must be at least 2 characters!";
        }

        if (!formData.lastName?.trim()) {
            errors.lastName = "Last Name is required!";
        } else if (formData.lastName.length < 2) {
            errors.lastName = "Last Name must be at least 2 characters!";
        }

        // Initials validation
        if (!formData.initials?.trim()) {
            errors.initials = "Initials is required!";
        } else if (formData.initials.length < 1) {
            errors.initials = "Initials must be at least 1 character!";
        }

        // Email validation
        if (!formData.email?.trim()) {
            errors.email = "Email is required!";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Please enter a valid email address!";
        }

        // Mobile validation
        if (!formData.mobile?.trim()) {
            errors.mobile = "Mobile number is required!";
        } else if (!/^\+?[\d\s-]{10,}$/.test(formData.mobile)) {
            errors.mobile = "Please enter a valid mobile number!";
        }

        // Password validations
        if (!formData.passwordHash?.trim()) {
            errors.passwordHash = "Password is required!";
        } else if (formData.passwordHash.length < 8) {
            errors.passwordHash = "Password must be at least 8 characters!";
        }

        if (!formData.confirmPassword?.trim()) {
            errors.confirmPassword = "Confirm Password is required!";
        } else if (formData.passwordHash !== formData.confirmPassword) {
            errors.confirmPassword = "Passwords do not match!";
        }

        return errors;
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const errors = validateForm();
        
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        manageUser({ ...formData, action: 'INSERT' , profilePhoto : "string" });
    };

    const handleFileChange = (e: any, fieldName: string) => {
        const file = e.target.files[0];

        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setFormErrors((prev: any) => ({
                    ...prev,
                    profileImage: "File size should not exceed 5MB"
                }));
                return;
            }

            // Validate file type
            if (!file.type.startsWith('image/')) {
                setFormErrors((prev: any) => ({
                    ...prev,
                    profileImage: "Please upload an image file"
                }));
                return;
            }

            setFormData((prevData: any) => ({
                ...prevData,
                [fieldName]: file,
            }));
            setFormErrors((prev: any) => ({
                ...prev,
                profileImage: ""
            }));
        }
    };

    return (
        <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <Form style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "15px", paddingLeft: "30px" }}>
                <div className="row">
                    <div className="d-flex justify-content-between align-items-center my-3">
                        <H3 className="heading-color f-w-900">Add Staff</H3>
                    </div>
                    {/* Role field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Role:"
                                name="roleID"
                                value={formData.roleID}
                                setValue={handleValueChange}
                                options={roleOptions}
                                passCol="col-10"
                                design="flex"
                                error={formErrors.roleID}
                                errorMessage={formErrors.roleID}
                            />
                        </Col>
                    </div>

                    {/* First Name field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="First Name:"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                setValue={handleValueChange}
                                placeholder="First Name"
                                passCol="col-10"
                                design="flex"
                                maxChars={20}
                                error={formErrors.firstName}
                                errorMessage={formErrors.firstName}
                            />
                        </Col>
                    </div>

                    {/* Last Name field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Last Name:"
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                setValue={handleValueChange}
                                placeholder="Last Name"
                                passCol="col-10"
                                design="flex"
                                maxChars={20}
                                error={formErrors.lastName}
                                errorMessage={formErrors.lastName}
                            />
                        </Col>
                    </div>

                    {/* Initials field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Initials:"
                                type="text"
                                name="initials"
                                value={formData.initials}
                                setValue={handleValueChange}
                                placeholder="Initials"
                                passCol="col-10"
                                design="flex"
                                maxChars={10}
                                error={formErrors.initials}
                                errorMessage={formErrors.initials}
                            />
                        </Col>
                    </div>

                    {/* Mobile field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <MobileField
                                label="Mobile:"
                                mobileField="mobile"
                                value={formData.mobile}
                                setValue={(field, value) => handleValueChange(field, value)}
                                placeholder="Mobile"
                                passCol="col-10"
                                design="flex"
                                maxLength={18}
                                error={formErrors.mobile}
                                errorMessage={formErrors.mobile}
                            />
                        </Col>
                    </div>

                    {/* Email field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Email:"
                                type="email"
                                name="email"
                                value={formData.email}
                                setValue={handleValueChange}
                                placeholder="Email"
                                passCol="col-10"
                                design="flex"
                                maxChars={50}
                                error={formErrors.email}
                                errorMessage={formErrors.email}
                            />
                        </Col>
                    </div>

                    {/* Password field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <div className="position-relative" style={{ width: '100%' }}>
                                <TextField
                                    label="Password:"
                                    type={showPassword ? "text" : "password"}
                                    name="passwordHash"
                                    value={formData.passwordHash}
                                    setValue={handleValueChange}
                                    placeholder="Password"
                                    passCol="col-10"
                                    design="flex"
                                    maxChars={50}
                                    error={formErrors.passwordHash}
                                    errorMessage={formErrors.passwordHash}
                                />
                                <div 
                                    className="position-absolute" 
                                    style={{ 
                                        right: "15px", 
                                        top: "50%", 
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        zIndex: 1,
                                        padding: "5px"
                                    }}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff size={18} color="#666" />
                                    ) : (
                                        <Eye size={18} color="#666" />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </div>

                    {/* Confirm Password field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <div className="position-relative" style={{ width: '100%' }}>
                                <TextField
                                    label="Confirm Password:"
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    setValue={handleValueChange}
                                    placeholder="Confirm Password"
                                    passCol="col-10"
                                    design="flex"
                                    maxChars={50}
                                    error={formErrors.confirmPassword}
                                    errorMessage={formErrors.confirmPassword}
                                />
                                <div 
                                    className="position-absolute" 
                                    style={{ 
                                        right: "15px", 
                                        top: "50%", 
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        zIndex: 1,
                                        padding: "5px"
                                    }}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff size={18} color="#666" />
                                    ) : (
                                        <Eye size={18} color="#666" />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </div>

                    <Col className="" style={{ marginBottom: "20px" }}>
                        <Label>Profile Image</Label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, "profileImage")}
                            style={{marginLeft : '50px'}}
                        />
                        {formErrors.profileImage && (
                            <div className="text-danger" style={{marginLeft: '50px'}}>
                                {formErrors.profileImage}
                            </div>
                        )}
                        {formData.profileImage && (
                            <img
                                src={URL.createObjectURL(formData.profileImage)}
                                alt="Profile"
                                style={{
                                    width: "200px",
                                    height: "200px",
                                    marginTop: "10px",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        )}
                    </Col>
                </div>

                <div className="d-flex gap-3">
                    <Link
                        to="/crmstaff"
                        style={{ color: "#0d6efd", cursor: "pointer", textDecoration: "none", fontWeight: "500" }}
                    >
                        Back to Staff |
                    </Link>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        disabled={isManageUserPending}
                        type="submit"
                    >
                        {isManageUserPending ? "Loading . . ." : "Save Staff"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default AddStaff;
